import { useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import {
  Alert,
  FormControlLabel,
  Grid,
  Switch,
  Checkbox,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useCompanyAction } from "../../hooks/useCompanyAction";
import { FileInput } from "../../../ui/components/FileInput";
import {
  FourEyesMode,
  FourEyesModeView,
  getS3UploadUrl,
  TCreateCompanyData,
  uploadToS3,
} from "../../network";
import { isEmpty } from "lodash";
import {
  AUTH_COMPANY_NAME,
  AUTH_COMPLIANCE_EMAIL,
  ICompanyActionModeType,
  TCompany,
} from "../../models";
import { ConfirmationDialog } from "../../../ui/components/ConfirmationDialog";
import { mainTheme } from "../../../../theme/mainTheme";
import { ActionType } from "../../../../models/ActionType";

export type Props = {
  onClose: (success: boolean) => void;
  open: boolean;
  actionMode?: ICompanyActionModeType;
  selectedCompany?: TCompany | null;
};

type FormValues = {
  name: string;
  purpose: string;
  picture?: File;
};

export const CreateCompanyDialog = (props: Props) => {
  const { onClose, open, actionMode = "create", selectedCompany } = props;
  const [result, loading, error, request, setError] =
    useCompanyAction(actionMode);
  const [isActive, setIsActive] = useState(true);
  const [fourEyesModeEnabled, setFourEyesModeEnabled] = useState(false);
  const [fourEyesMode, setFourEyesMode] = useState(FourEyesMode.DISABLED);
  const [name, setName] = useState("");
  const [complianceEmail, setComplianceEmail] = useState("");
  const [purpose, setPurpose] = useState("");
  const [picture, setPicture] = useState<File | string | null>(null);
  const [errors, setErrors] = useState<any>({});
  const currentCompany = localStorage.getItem(AUTH_COMPANY_NAME);

  const onChangeFourEyesModeEnabled = () => {
    if (!fourEyesModeEnabled) {
      setFourEyesModeEnabled(true);
      setFourEyesMode(FourEyesMode.STRICT);
    } else {
      setFourEyesModeEnabled(false);
      setFourEyesMode(FourEyesMode.DISABLED);
    }
  };

  const handleCancel = useCallback(
    (success: boolean = false) => {
      setIsActive(true);
      setPicture(null);
      setErrors({});
      setError(null);
      onClose(success);
    },
    [onClose, setError]
  );

  useEffect(() => {
    if (result) {
      handleCancel(true);
    }
  }, [result, handleCancel]);

  useEffect(() => {
    if (selectedCompany) {
      setIsActive(selectedCompany.isActive);
      setName(selectedCompany.name);
      setPurpose(selectedCompany.purpose);
      setPicture(selectedCompany.picture);
      setComplianceEmail(selectedCompany.complianceEmail);
      setFourEyesMode(selectedCompany.fourEyesMode);
      setFourEyesModeEnabled(
        selectedCompany.fourEyesMode !== FourEyesMode.DISABLED
      );
    }
  }, [selectedCompany]);

  const validate = useCallback((formData: FormValues) => {
    const errors: any = {};

    if (!formData.name) {
      errors.name = true;
    }
    if (!formData.purpose) {
      errors.purpose = true;
    }
    setErrors(errors);
    return errors;
  }, []);

  const onSubmit = useCallback(async () => {
    const formValues: FormValues = {
      name,
      purpose,
    };
    const validateErrors = validate(formValues);
    if (isEmpty(validateErrors)) {
      const requestData: TCreateCompanyData = {
        name,
        purpose,
        isActive,
        fourEyesMode,
      };
      if (picture) {
        if (typeof picture === "object") {
          try {
            const { key, url } = await getS3UploadUrl(
              picture.type,
              picture.size,
              selectedCompany!.id
            );
            await uploadToS3(url, picture, picture.type);
            requestData.picture = key;
          } catch (e) {
            console.error(e);
          }
        }
      }
      if (complianceEmail) {
        requestData.complianceEmail = complianceEmail;
      }
      if (actionMode === ActionType.EDIT) {
        requestData.id = selectedCompany!.id;
      }
      if (selectedCompany?.name === currentCompany) {
        localStorage.setItem(AUTH_COMPANY_NAME, name);
        localStorage.setItem(AUTH_COMPLIANCE_EMAIL, complianceEmail);
      }
      request(requestData);
    }
  }, [
    request,
    actionMode,
    validate,
    name,
    picture,
    purpose,
    selectedCompany,
    isActive,
    complianceEmail,
    currentCompany,
    fourEyesMode,
  ]);

  if (actionMode === "delete") {
    return (
      <ConfirmationDialog
        title='Are you sure?'
        description={`Are you sure to delete company ${name}`}
        error={error}
        confirmText='Delete'
        confirmAction={() => request(selectedCompany!.id)}
        disabled={loading}
        onClose={() => handleCancel(false)}
        isOpen={open}
        confirmButtonColor='error'
        titleColor={mainTheme.palette.error.main}
      />
    );
  }

  return (
    <Dialog
      sx={{ "& .MuiDialog-paper": { width: "80%", maxHeight: 535 } }}
      maxWidth='xs'
      open={open}
      id='create-company-dialog'
      keepMounted
    >
      <DialogTitle sx={{ textTransform: "capitalize" }}>
        {actionMode} company
      </DialogTitle>
      <DialogContent dividers>
        <Grid container direction='column'>
          <FormControlLabel
            control={<Switch />}
            label={isActive ? "Active" : "Inactive"}
            name={"isActive"}
            checked={isActive}
            onChange={() => setIsActive((prev) => !prev)}
          />
          <FormControlLabel
            control={<Checkbox />}
            label={"Switch on 4-eyes"}
            name={"switchOn4Eyes"}
            checked={fourEyesModeEnabled}
            onChange={onChangeFourEyesModeEnabled}
          />
          {fourEyesModeEnabled && (
            <FormControl required>
              <InputLabel>4-eyes mode</InputLabel>
              <Select
                variant='filled'
                name={"declineReason"}
                label={"Decline reason"}
                value={fourEyesMode}
                renderValue={(value) => FourEyesModeView[value]}
                onChange={(e) =>
                  setFourEyesMode(e.target.value as FourEyesMode)
                }
              >
                {[FourEyesMode.STRICT, FourEyesMode.OPTIONAL].map((mode) => (
                  <MenuItem value={mode} key={mode}>
                    {FourEyesModeView[mode]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            error={errors.name}
            label='Company name'
            name='name'
            required={true}
            size='small'
            margin='normal'
            value={name}
            inputProps={{
              onChange: (e: any) => {
                delete errors.name;
                return setName(e.target.value);
              },
            }}
          />
          <TextField
            error={errors.purpose}
            label='Purpose of relationship for onboarded users'
            name='purpose'
            required={true}
            size='small'
            margin='normal'
            value={purpose}
            inputProps={{
              onChange: (e: any) => {
                delete errors.purpose;
                return setPurpose(e.target.value);
              },
            }}
          />
          <FileInput
            label='Logo'
            namePrefix='picture'
            handleChange={setPicture}
            value={picture}
          />
          <TextField
            label='Compliance e-mail'
            name='complianceEmail'
            size='small'
            margin='normal'
            value={complianceEmail}
            inputProps={{
              onChange: (e: any) => {
                return setComplianceEmail(e.target.value);
              },
            }}
          />
        </Grid>
        {error !== null && <Alert severity='error'>{error.toString()}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={() => handleCancel()} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={loading}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
